import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Edit3, Send, X, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import './ProposalViewer.css';

const ProposalViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedText, setSelectedText] = useState('');
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentPosition, setCommentPosition] = useState({ x: 0, y: 0 });
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const slideRef = useRef(null);

  useEffect(() => {
    fetchProposal();
    fetchComments();
  }, [id]);

  const fetchProposal = async () => {
    try {
      const response = await api.get(`/proposals/${id}/content`);
      setProposal(response.data.proposal);
      setSlides(response.data.slides || []);
    } catch (error) {
      console.error('Error fetching proposal:', error);
      toast.error('Error loading proposal');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/proposals/${id}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleSlideClick = (e) => {
    if (e.target.closest('.comment-marker') || e.target.closest('.slide-actions')) {
      return;
    }

    const rect = slideRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setCommentPosition({ x, y });
    setShowCommentModal(true);
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    
    if (text) {
      setSelectedText(text);
      setShowAIModal(true);
    }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;

    try {
      const response = await api.post(`/proposals/${id}/comment`, {
        slide_number: currentSlide,
        x_position: commentPosition.x,
        y_position: commentPosition.y,
        text: commentText
      });

      const newComment = response.data;
      setComments([...comments, newComment]);
      setCommentText('');
      setShowCommentModal(false);
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Error adding comment');
    }
  };

  const editTextWithAI = async () => {
    if (!aiPrompt.trim()) return;

    setAiLoading(true);
    try {
      const response = await api.post(`/proposals/${id}/edit-text`, {
        slide_number: currentSlide,
        selected_text: selectedText,
        prompt: aiPrompt
      });

      const result = response.data;
      // Update the slide content with AI-generated text
      const updatedSlides = [...slides];
      updatedSlides[currentSlide] = result.updated_slide;
      setSlides(updatedSlides);
      setAiPrompt('');
      setShowAIModal(false);
      setSelectedText('');
      toast.success('Text updated with AI');
    } catch (error) {
      console.error('Error editing text:', error);
      toast.error('Error editing text');
    } finally {
      setAiLoading(false);
    }
  };

  const editSlideWithAI = async () => {
    if (!aiPrompt.trim()) return;

    setAiLoading(true);
    try {
      const response = await api.post(`/proposals/${id}/edit-slide`, {
        slide_number: currentSlide,
        prompt: aiPrompt
      });

      const result = response.data;
      const updatedSlides = [...slides];
      updatedSlides[currentSlide] = result.updated_slide;
      setSlides(updatedSlides);
      setAiPrompt('');
      setShowAIModal(false);
      toast.success('Slide updated with AI');
    } catch (error) {
      console.error('Error editing slide:', error);
      toast.error('Error editing slide');
    } finally {
      setAiLoading(false);
    }
  };

  const getCommentsForSlide = (slideNumber) => {
    return comments.filter(comment => comment.slide_number === slideNumber);
  };

  if (loading) {
    return (
      <div className="proposal-viewer-container">
        <div className="loading">Loading proposal...</div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="proposal-viewer-container">
        <div className="error">Proposal not found</div>
      </div>
    );
  }

  return (
    <div className="proposal-viewer-container">
      {/* Header */}
      <div className="viewer-header">
        <button onClick={() => navigate('/proposals')} className="back-button">
          <ArrowLeft size={20} />
          Back to Proposals
        </button>
        <h1>{proposal.filename}</h1>
        <div className="slide-navigation">
          <span>Slide {currentSlide + 1} of {slides.length}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="viewer-content">
        {/* Slide Display */}
        <div className="slide-container">
          <div 
            ref={slideRef}
            className="slide-content"
            onClick={handleSlideClick}
            onMouseUp={handleTextSelection}
          >
            {slides[currentSlide] && (
              <div dangerouslySetInnerHTML={{ __html: slides[currentSlide].content }} />
            )}
            
            {/* Comment Markers */}
            {getCommentsForSlide(currentSlide).map((comment) => (
              <div
                key={comment.id}
                className="comment-marker"
                style={{
                  left: comment.x_position,
                  top: comment.y_position
                }}
                title={comment.text}
              >
                <MessageCircle size={16} />
              </div>
            ))}
          </div>

          {/* Slide Actions */}
          <div className="slide-actions">
            <button 
              className="action-button"
              onClick={() => setShowAIModal(true)}
            >
              <Edit3 size={16} />
              AI Edit Slide
            </button>
          </div>
        </div>

        {/* Comments Panel */}
        <div className="comments-panel">
          <h3>Comments</h3>
          <div className="comments-list">
            {getCommentsForSlide(currentSlide).length === 0 ? (
              <p className="no-comments">No comments on this slide</p>
            ) : (
              getCommentsForSlide(currentSlide).map((comment) => (
                <div key={comment.id} className="comment-item">
                  <div className="comment-text">{comment.text}</div>
                  <div className="comment-meta">
                    {new Date(comment.timestamp).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add Comment</h3>
              <button onClick={() => setShowCommentModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Enter your comment..."
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowCommentModal(false)}>Cancel</button>
              <button onClick={addComment} className="primary">
                <Send size={16} />
                Add Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Edit Modal */}
      {showAIModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{selectedText ? 'Edit Selected Text' : 'AI Edit Slide'}</h3>
              <button onClick={() => setShowAIModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-content">
              {selectedText && (
                <div className="selected-text">
                  <strong>Selected Text:</strong>
                  <p>"{selectedText}"</p>
                </div>
              )}
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={selectedText ? "How would you like to edit this text?" : "Describe how you want to edit this slide..."}
                rows={4}
              />
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowAIModal(false)}>Cancel</button>
              <button 
                onClick={selectedText ? editTextWithAI : editSlideWithAI} 
                className="primary"
                disabled={aiLoading}
              >
                {aiLoading ? 'Processing...' : (
                  <>
                    <Edit3 size={16} />
                    {selectedText ? 'Edit Text' : 'Edit Slide'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalViewer; 